import { useMemo } from 'react';
import { format, subDays } from 'date-fns';
import { useRouter } from 'next/router';
import { useDateNow, useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { useApiData } from 'lib/api';
import { Box } from 'components/common/box';
import { BarChart, DateRangeSelector } from 'components/common/charts';
import { StatItem } from 'components/common/stats';
import { CoursesLanguageDropdown } from 'components/courses';

import type { GetCourseCompletionsRes } from 'shared-types/responses';

const FollowCompletions = () => {
  const { dateNow } = useDateNow();

  const { messages, pageState } = useReduxState();

  const router = useRouter();

  // get filters from URL
  const currentLanguageVersion =
    typeof router.query.languageVersion === 'string'
      ? router.query.languageVersion
      : 'all';
  const fromDate =
    typeof router.query.fromDate === 'string'
      ? router.query.fromDate
      : `${format(subDays(dateNow, 30), 'y-MM-dd')}T00:00:00`;
  const toDate =
    typeof router.query.toDate === 'string'
      ? router.query.toDate
      : `${format(dateNow, 'y-MM-dd')}T23:59:59`;

  const { data, loading } = useApiData<GetCourseCompletionsRes>({
    url:
      `/admin/analytics/course-completions` +
      `?courseId=${pageState?.course?.id}` +
      `&fromDate=${fromDate}` +
      `&toDate=${toDate}` +
      (Object.keys(pageState?.course?.languageVersions || {}).length > 0 &&
      currentLanguageVersion !== 'all'
        ? `&languageVersion=${currentLanguageVersion}`
        : '')
  });

  const handleLanguageVersionFilter = (value: string) => {
    const query = { ...router.query };
    if (value === 'all') delete query.languageVersion;
    else query.languageVersion = value;

    router.replace({ query }, undefined, { shallow: true });
  };

  const handleDateRangeChange = ({
    fromDate,
    toDate
  }: {
    fromDate: string;
    toDate: string;
  }) => {
    router.replace(
      { query: { ...router.query, fromDate, toDate } },
      undefined,
      { shallow: true }
    );
  };

  const completions = useMemo(
    () =>
      data?.completions?.data?.length
        ? data.completions.data.map((entry) => ({
            label: entry.label,
            [messages.commonCourseCompletions]: entry.count
          }))
        : [],
    [data]
  );

  return (
    <div className="chart">
      <Box
        loading={loading}
        headerContent={
          <div className="filters">
            <DateRangeSelector
              size="header"
              updateData={handleDateRangeChange}
            />

            {Object.keys(pageState.course.languageVersions).length > 0 && (
              <CoursesLanguageDropdown
                category="lighter"
                currentLanguage={currentLanguageVersion}
                includeAllOption={true}
                languageOptions={[
                  pageState.course.language,
                  ...Object.keys(pageState.course.languageVersions)
                ]}
                onChange={handleLanguageVersionFilter}
                renderInPortal={true}
              />
            )}
          </div>
        }
        headerContentWrap={true}
        title={messages.commonCourseCompletions}
      >
        <div className="total-completions">
          <StatItem
            align="left"
            horizontal={true}
            label={messages.analyticsCourseCompletionsInTotal}
            value={data?.completions?.totalCount}
          />
        </div>

        <BarChart
          data={completions}
          xAxisDataKey="label"
          yAxisDataKey={messages.commonCourseCompletions}
        />
      </Box>

      <style jsx>{`
        .filters {
          align-items: center;
          display: flex;
          flex-wrap: wrap;
          justify-content: flex-end;
          gap: 20px;
          width: 100%;
        }
        .total-completions {
          padding: 10px 5px;
        }
        @media (max-width: ${breakpoints.small}px) {
          .filters {
            align-items: flex-start;
            flex-direction: column;
            justify-content: flex-start;
            gap: 10px;
          }
        }
      `}</style>
    </div>
  );
};

export default FollowCompletions;
